// src/pages/MsoCalculatorPage.tsx
//
// ★★ ANNEX A 22 — the MSO pro-ration calculator.
//
// An input form over `POST /api/mso-calculations`. The route RECORDS every attempt, a refusal as
// much as an answer, so this screen says so: running a case here is not a rehearsal, it leaves a
// calculation in the enquiry, and the result card links to it.
//
// ══ WHAT HAPPENS ON PRESSING CALCULATE ══
//
// * **Typing mistakes are caught here and never recorded.** A date that is not a real date, a reference of the
//   wrong shape, a component with no whole-cent amount: `checkMsoForm` reports them in the form, under the
//   field, exactly as a server refusal would, and nothing is posted, so no REFUSED calculation is stored for a
//   slip of the keyboard. What the bill MEANS is still the engine's to judge, recorded and explained.
// * **The button is never disabled while a request runs.** A disabled control drops keyboard focus to the
//   page body. It carries `aria-busy` instead, and a ref guards against a second press (three quick clicks
//   used to store three calculations). When the answer renders, focus moves to its heading.
//
// ══ WHAT THIS SCREEN DOES NOT DO ══
//
// * **No arithmetic on a share.** The amounts and the trace are the engine's own. The only
//   computation here is turning the dollars an officer typed into whole cents, by splitting the
//   decimal string (`lib/mso-calculator-form.ts`), because the route refuses a dollar string.
// * **No guessed default.** A field left blank is omitted from the request, so the engine can
//   answer INCOMPLETE_INPUT naming it. Booleans are three-state (not supplied / yes / no) for
//   exactly that reason: a lone checkbox cannot say "not supplied", and an unticked box sent as
//   `false` would turn a missing answer into a definite one.
// * **A refusal is shown, not hidden.** A 400 (INVALID_INPUT) and a 422 (the rest) both come back
//   as a stored calculation; `api.calculateMso` resolves with that body so the card, the fields it
//   names and the trace are all on screen.
import { plainProse } from '../lib/plain-words'
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { DatePicker } from '../components/DatePicker'
import { FieldError, fieldErrorProps } from '../components/FieldError'
import { ErrorCard, LoadingCard } from '../components/StateCards'
import { MsoCalculationResult } from '../components/MsoCalculationResult'
import {
  blankForm,
  checkMsoForm,
  displayedTotal,
  formFromInput,
  refusalNames,
  type CalculatorForm,
  type ComponentRowForm,
} from '../lib/mso-calculator-form'
import {
  humaniseToken,
  MSO_BENEFICIARY_TYPES,
  MSO_BILL_INDICATORS,
  MSO_CAP_STATUSES,
  MSO_CARE_SETTINGS,
  MSO_COMPONENTS,
  MSO_COMPONENT_LABELS,
  MSO_FIELD_LABELS,
  MSO_HSP_TYPES,
  MSO_SPECIAL_CONDITIONS,
  MSO_VISIT_TYPES,
} from '../lib/mso-vocabulary'
import type { MsoCalculateResponse, MsoTestCase } from '../lib/types'
import { NETWORK_FAILURE } from '../lib/messages'

const ENUM_CONTROLS: readonly { readonly name: string; readonly values: readonly string[] }[] = [
  { name: 'beneficiaryType', values: MSO_BENEFICIARY_TYPES },
  { name: 'careSetting', values: MSO_CARE_SETTINGS },
  { name: 'visitType', values: MSO_VISIT_TYPES },
  { name: 'hspType', values: MSO_HSP_TYPES },
  { name: 'capStatus', values: MSO_CAP_STATUSES },
  { name: 'billIndicator', values: MSO_BILL_INDICATORS },
  { name: 'specialBillCondition', values: MSO_SPECIAL_CONDITIONS },
]

const FLAGGED = 'Named in the refusal above.'

/** Every Authority pack case, read from the active version of each rule. */
async function loadPackCases(): Promise<readonly MsoTestCase[]> {
  const { rules } = await api.msoRules()
  const active = rules.flatMap((rule) => (rule.active === null ? [] : [rule.active.id]))
  const detail = await Promise.all(active.map((id) => api.msoRuleVersion(id)))
  return detail
    .flatMap((d) => d.testCases)
    .filter((c) => c.source === 'AUTHORITY_PACK')
    .sort((a, b) => (a.caseId < b.caseId ? -1 : a.caseId > b.caseId ? 1 : 0))
}

export function MsoCalculatorPage() {
  const baseId = useId()
  const [form, setForm] = useState<CalculatorForm>(blankForm)
  const [loadedCase, setLoadedCase] = useState('')
  const [formErrors, setFormErrors] = useState<Readonly<Record<string, string>>>({})
  const [busy, setBusy] = useState(false)
  // A ref, not state: a second click in the same tick must see the first one's flag at once.
  const inFlight = useRef(false)
  const resultHeadingRef = useRef<HTMLHeadingElement>(null)
  const problemSummaryRef = useRef<HTMLParagraphElement>(null)
  const [result, setResult] = useState<MsoCalculateResponse | null>(null)
  const [transportError, setTransportError] = useState<string | null>(null)

  const cases = useApi(loadPackCases, [])

  // After the answer renders, focus goes to its heading so a keyboard or screen-reader user lands on it.
  useEffect(() => {
    if (result === null) return
    // The form is about 1,500px tall, so the answer lands below the fold. Bring it into view AND focus it.
    resultHeadingRef.current?.scrollIntoView?.({ block: 'start' })
    resultHeadingRef.current?.focus({ preventScroll: true })
  }, [result])

  const named = result !== null && result.evaluation.outcome === 'REFUSED' ? (result.evaluation.refusal?.fields ?? []) : []
  const flagged = (name: string): boolean => refusalNames(named, name)
  const problemFor = (name: string): string | null => formErrors[name] ?? (flagged(name) ? FLAGGED : null)

  const setField = (name: string, value: string) =>
    setForm((f) => ({ ...f, fields: { ...f.fields, [name]: value } }))
  const setComponentRow = (index: number, patch: Partial<ComponentRowForm>) =>
    setForm((f) => ({ ...f, components: f.components.map((row, i) => (i === index ? { ...row, ...patch } : row)) }))

  const loadCase = (caseId: string) => {
    setLoadedCase(caseId)
    const chosen = (cases.data ?? []).find((c) => c.caseId === caseId)
    if (chosen === undefined) return
    setForm(formFromInput(chosen.input))
    setFormErrors({})
    setResult(null)
    setTransportError(null)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (inFlight.current) return
    const built = checkMsoForm(form)
    if (built.errors !== undefined) {
      setFormErrors(built.errors)
      setResult(null)
      setTransportError(null)
      // Focus the summary so the problems are announced and the first one is a Tab away.
      setTimeout(() => problemSummaryRef.current?.focus(), 0)
      return
    }
    setFormErrors({})
    inFlight.current = true
    setBusy(true)
    setTransportError(null)
    try {
      setResult(await api.calculateMso(built.request))
    } catch (error) {
      setResult(null)
      setTransportError(error instanceof ApiError ? error.message : `The calculation could not be sent. ${NETWORK_FAILURE}`)
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const dateProps = (name: string) => {
    const problem = problemFor(name)
    return problem === null ? {} : { invalid: true, describedBy: errId(name) }
  }
  const problemCount = Object.keys(formErrors).length

  const fieldId = (name: string) => `${baseId}-${name.replace(/[^A-Za-z0-9]/g, '-')}`
  const errId = (name: string) => `${fieldId(name)}-error`

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">MSO calculator</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Price one MSO bill against the rules in force on the date of treatment. The engine explains every
          step, and it refuses a bill it cannot price rather than guessing. Every calculation, answered or refused, is
          recorded and appears in the calculation enquiry.
        </p>
      </div>

      <form className="pt-card flex flex-col gap-5" onSubmit={handleSubmit} noValidate aria-label="MSO calculation input">
        <div className="max-w-md">
          <label className="flex flex-col gap-1 text-sm" htmlFor={`${baseId}-case`}>
            <span className="font-semibold">Load a case</span>
          </label>
          <select
            id={`${baseId}-case`}
            className="pt-native-date"
            value={loadedCase}
            onChange={(e) => loadCase(e.target.value)}
            aria-describedby={`${baseId}-case-hint`}
          >
            <option value="">Start from a blank form</option>
            {(cases.data ?? []).map((c) => (
              <option key={`${c.ruleKey}-${c.caseId}`} value={c.caseId}>
                {c.caseId}: {plainProse(c.description)}
              </option>
            ))}
          </select>
          <p id={`${baseId}-case-hint`} className="text-xs text-[var(--sgds-body-color-subtle)] mt-1">
            Fills every field below from a ready-made case. You can change any field before you calculate.
          </p>
          {cases.initialLoading && <LoadingCard label="Loading the cases" rows={1} />}
          {cases.error !== null && (
            <p role="alert" className="text-sm text-[var(--sgds-danger-color-emphasis)] mt-1">
              The cases could not be loaded: {cases.error} The form still works without them.
            </p>
          )}
        </div>

        <fieldset className="grid gap-4 sm:grid-cols-2 border-0 p-0 m-0">
          <legend className="text-lg font-semibold mb-2">The bill</legend>

          <div className="flex flex-col gap-1 text-sm">
            <span id={`${baseId}-date-label`} className="font-semibold">
              Date of treatment <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker
              value={form.treatmentDate}
              onChange={(v) => setForm((f) => ({ ...f, treatmentDate: v }))}
              labelledBy={`${baseId}-date-label`}
              required
              subject="treatment date"
              {...dateProps('treatmentDate')}
            />
            <FieldError id={errId('treatmentDate')} problem={problemFor('treatmentDate')} />
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={fieldId('beneficiaryRef')} className="font-semibold">
              Beneficiary reference (optional)
            </label>
            <input
              id={fieldId('beneficiaryRef')}
              type="text"
              className="pt-native-date"
              value={form.beneficiaryRef}
              placeholder="e.g. BEN-0001"
              onChange={(e) => setForm((f) => ({ ...f, beneficiaryRef: e.target.value }))}
              {...fieldErrorProps(errId('beneficiaryRef'), problemFor('beneficiaryRef'))}
            />
            <FieldError id={errId('beneficiaryRef')} problem={problemFor('beneficiaryRef')} />
            <span className="text-xs text-[var(--sgds-body-color-subtle)]">
              Stored with the calculation. Use a reference, never a name or an identity number.
            </span>
          </div>

          {/*
            ★ THE FIELDS ARE GROUPED BY WHAT READS THEM, not listed in one wall. Eleven inputs sat in a single grid with
            the same label weight and "Not supplied" in every select, so a first-time officer could not tell which ones
            decide the price. They now fall under three headings that follow the rules themselves: the fields every bill
            has, the outpatient details the outpatient rules read, and the inpatient details the inpatient rule reads.
            Every field, label and id is unchanged; only the grouping and the one-line hint under each heading are new.
          */}
          {(() => {
            const enumField = (name: string) => {
              const values = ENUM_CONTROLS.find((c) => c.name === name)?.values ?? []
              return (
                <div key={name} className="flex flex-col gap-1 text-sm">
                  <label htmlFor={fieldId(name)} className="font-semibold">
                    {MSO_FIELD_LABELS[name]}
                  </label>
                  <select
                    id={fieldId(name)}
                    className="pt-native-date"
                    value={form.fields[name] ?? ''}
                    onChange={(e) => setField(name, e.target.value)}
                    {...fieldErrorProps(errId(name), problemFor(name))}
                  >
                    <option value="">Not supplied</option>
                    {values.map((v) => (
                      <option key={v} value={v}>
                        {humaniseToken(v)}
                      </option>
                    ))}
                  </select>
                  <FieldError id={errId(name)} problem={problemFor(name)} />
                </div>
              )
            }
            const yesNoField = (name: string) => (
              <div key={name} className="flex flex-col gap-1 text-sm">
                <label htmlFor={fieldId(name)} className="font-semibold">
                  {MSO_FIELD_LABELS[name]}
                </label>
                <select
                  id={fieldId(name)}
                  className="pt-native-date"
                  value={form.fields[name] ?? ''}
                  onChange={(e) => setField(name, e.target.value)}
                  {...fieldErrorProps(errId(name), problemFor(name))}
                >
                  <option value="">Not supplied</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <FieldError id={errId(name)} problem={problemFor(name)} />
              </div>
            )
            const wardField = (name: string) => (
              <div key={name} className="flex flex-col gap-1 text-sm">
                <label htmlFor={fieldId(name)} className="font-semibold">
                  {MSO_FIELD_LABELS[name]}
                </label>
                <input
                  id={fieldId(name)}
                  type="text"
                  className="pt-native-date"
                  value={form.fields[name] ?? ''}
                  placeholder="e.g. B1"
                  onChange={(e) => setField(name, e.target.value)}
                  {...fieldErrorProps(errId(name), problemFor(name))}
                />
                <FieldError id={errId(name)} problem={problemFor(name)} />
              </div>
            )
            const group = (title: string, hint: string, children: ReactNode) => (
              <div className="sm:col-span-2 flex flex-col gap-3 border-t border-[var(--sgds-border-color-muted)] pt-4" role="group" aria-label={title}>
                <div>
                  <h2 className="text-base font-semibold m-0">{title}</h2>
                  <p className="text-xs text-[var(--sgds-body-color-subtle)] m-0">{hint}</p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">{children}</div>
              </div>
            )
            return (
              <>
                {group(
                  'Every bill',
                  'Who the bill is for and the kind of care. Most rules look at these first.',
                  <>
                    {enumField('beneficiaryType')}
                    {enumField('careSetting')}
                    {enumField('capStatus')}
                    {enumField('billIndicator')}
                  </>,
                )}
                {group(
                  'Outpatient bills',
                  'The visit and the provider. The outpatient rules read these.',
                  <>
                    {enumField('visitType')}
                    {enumField('hspType')}
                    {yesNoField('governmentReferral')}
                  </>,
                )}
                {group(
                  'Inpatient bills',
                  'The condition and the ward. The inpatient rule reads these.',
                  <>
                    {enumField('specialBillCondition')}
                    {wardField('eligibleWardClass')}
                    {wardField('billedWardClass')}
                    {yesNoField('voluntaryWardUpgrade')}
                  </>,
                )}
              </>
            )
          })()}
        </fieldset>

        <fieldset className="grid gap-4 sm:grid-cols-3 border-0 p-0 m-0">
          <legend className="text-lg font-semibold mb-2">Entitlement</legend>
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={fieldId('entitlement.scheme')} className="font-semibold">
              Scheme
            </label>
            <input
              id={fieldId('entitlement.scheme')}
              type="text"
              className="pt-native-date"
              value={form.entitlementScheme}
              placeholder="MSO"
              onChange={(e) => setForm((f) => ({ ...f, entitlementScheme: e.target.value }))}
              {...fieldErrorProps(errId('entitlement.scheme'), problemFor('entitlement.scheme') ?? problemFor('entitlement'))}
            />
            <FieldError id={errId('entitlement.scheme')} problem={problemFor('entitlement.scheme') ?? problemFor('entitlement')} />
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <span id={`${baseId}-from-label`} className="font-semibold">
              Valid from
            </span>
            <DatePicker
              value={form.validFrom}
              onChange={(v) => setForm((f) => ({ ...f, validFrom: v }))}
              labelledBy={`${baseId}-from-label`}
              subject="entitlement start date"
              {...dateProps('entitlement.validFrom')}
            />
            <FieldError id={errId('entitlement.validFrom')} problem={problemFor('entitlement.validFrom')} />
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <span id={`${baseId}-until-label`} className="font-semibold">
              Valid until (blank for open-ended)
            </span>
            <DatePicker
              value={form.validUntil}
              onChange={(v) => setForm((f) => ({ ...f, validUntil: v }))}
              labelledBy={`${baseId}-until-label`}
              subject="entitlement end date"
              {...dateProps('entitlement.validUntil')}
            />
            <FieldError id={errId('entitlement.validUntil')} problem={problemFor('entitlement.validUntil')} />
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-3 border-0 p-0 m-0">
          <legend className="text-lg font-semibold mb-2">Amounts (SGD)</legend>
          <FieldError id={errId('components')} problem={problemFor('components')} />
          {form.components.map((row, index) => {
            const amountKey = `components[${index}].amountCents`
            const componentKey = `components[${index}].component`
            const occurrencesKey = `components[${index}].occurrences`
            return (
              <div key={index} className="grid gap-3 sm:grid-cols-[1fr_1fr_8rem_auto] items-start" data-row={index}>
                <div className="flex flex-col gap-1 text-sm">
                  <label htmlFor={fieldId(`c${index}-component`)} className="font-semibold">
                    Component {index + 1}
                  </label>
                  <select
                    id={fieldId(`c${index}-component`)}
                    className="pt-native-date"
                    value={row.component}
                    onChange={(e) => setComponentRow(index, { component: e.target.value })}
                    {...fieldErrorProps(errId(componentKey), problemFor(componentKey))}
                  >
                    {MSO_COMPONENTS.map((c) => (
                      <option key={c} value={c}>
                        {MSO_COMPONENT_LABELS[c]}
                      </option>
                    ))}
                  </select>
                  <FieldError id={errId(componentKey)} problem={problemFor(componentKey)} />
                </div>
                <div className="flex flex-col gap-1 text-sm">
                  <label htmlFor={fieldId(`c${index}-amount`)} className="font-semibold">
                    Amount {index + 1}
                  </label>
                  <input
                    id={fieldId(`c${index}-amount`)}
                    type="text"
                    inputMode="decimal"
                    className="pt-native-date"
                    value={row.amount}
                    placeholder="e.g. 135.20"
                    onChange={(e) => setComponentRow(index, { amount: e.target.value })}
                    {...fieldErrorProps(errId(amountKey), problemFor(amountKey))}
                  />
                  <FieldError id={errId(amountKey)} problem={problemFor(amountKey)} />
                </div>
                <div className="flex flex-col gap-1 text-sm">
                  <label htmlFor={fieldId(`c${index}-occurrences`)} className="font-semibold">
                    Occurrences {index + 1}
                  </label>
                  <input
                    id={fieldId(`c${index}-occurrences`)}
                    type="text"
                    inputMode="numeric"
                    className="pt-native-date"
                    value={row.occurrences}
                    placeholder="optional"
                    onChange={(e) => setComponentRow(index, { occurrences: e.target.value })}
                    {...fieldErrorProps(errId(occurrencesKey), problemFor(occurrencesKey))}
                  />
                  <FieldError id={errId(occurrencesKey)} problem={problemFor(occurrencesKey)} />
                </div>
                <div className="sm:mt-7">
                  <SgdsButton
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={form.components.length <= 1}
                    onClick={() => setForm((f) => ({ ...f, components: f.components.filter((_, i) => i !== index) }))}
                    aria-label={`Remove component ${index + 1}`}
                  >
                    {/* The accessible name has to be in the slotted text: SGDS puts the host's aria-label nowhere a screen reader looks. */}
                    Remove<span className="sr-only"> component {index + 1}</span>
                  </SgdsButton>
                </div>
              </div>
            )
          })}
          <div>
            <SgdsButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setForm((f) => ({ ...f, components: [...f.components, { component: 'BILL_AMOUNT', amount: '', occurrences: '' }] }))
              }
            >
              Add a component
            </SgdsButton>
          </div>
          <div className="flex flex-col gap-1 text-sm max-w-xs">
            <label htmlFor={fieldId('totalCents')} className="font-semibold">
              Total of the bill
            </label>
            <input
              id={fieldId('totalCents')}
              type="text"
              inputMode="decimal"
              className="pt-native-date"
              value={displayedTotal(form)}
              placeholder="Sum of the components"
              onChange={(e) => setForm((f) => ({ ...f, total: e.target.value, totalTouched: true }))}
              {...fieldErrorProps(errId('totalCents'), problemFor('totalCents'))}
            />
            <FieldError id={errId('totalCents')} problem={problemFor('totalCents')} />
            <span className="text-xs text-[var(--sgds-body-color-subtle)]">
              Follows the sum of the components, and falls back to it if you clear this box. The engine refuses a total that does not match.
            </span>
          </div>
        </fieldset>

        {problemCount > 0 && (
          <p ref={problemSummaryRef} tabIndex={-1} role="alert" className="text-sm font-semibold text-[var(--sgds-danger-color-emphasis)] m-0">
            {problemCount === 1 ? 'One field needs attention before this can be calculated.' : `${problemCount} fields need attention before this can be calculated.`} Nothing was sent or recorded.
          </p>
        )}

        <div className="pt-sticky-action">
          <SgdsButton type="submit" variant="primary" aria-busy={busy ? 'true' : 'false'}>
            {busy ? 'Calculating' : 'Calculate'}
          </SgdsButton>
        </div>
      </form>

      <div aria-live="polite" aria-busy={busy}>
        {transportError !== null && <ErrorCard title="The calculation could not be completed" message={transportError} />}
        {result !== null && <MsoCalculationResult result={result} headingRef={resultHeadingRef} />}
      </div>
    </div>
  )
}
